import React, { FC, useRef, useState } from 'react';

import {
	BUTTON_TYPES,
	DropdownOptionType,
	EvDropdownMenu,
	EvIcon,
	ThreeDotsIcon,
	TOAST_STATUS
} from '@evinced-private/ui-common';

import { DeleteConfirmationDialog } from 'src/common/components/delete-confirmation-dialog';
import { DELETE_CONFIRMATION_DIALOG_LABELS } from 'src/common/components/delete-confirmation-dialog/DeleteConfirmationDialog.labels';
import { useNotifications } from 'src/common/providers/notificationsProvider/NotificationsConsumer';

import { DELETE_LABEL_BY_ENTITY } from './RowActionsMenu.constants';
import { IRowActionsMenuProps } from './RowActionsMenu.types';

export const RowActionsMenu: FC<IRowActionsMenuProps> = ({
	id,
	triggerAriaLabel,
	onDelete,
	entityName = 'session',
	isDeleteDisabled = false
}) => {
	const [isConfirmOpen, setIsConfirmOpen] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);
	const { toast } = useNotifications();
	const isTabKeyRef = useRef(false);
	const isMenuOpenRef = useRef(false);

	const deleteLabel = DELETE_LABEL_BY_ENTITY[entityName];

	const handleConfirm = async (): Promise<void> => {
		setIsDeleting(true);
		try {
			await onDelete();
			setIsConfirmOpen(false);
		} catch {
			toast({
				show: true,
				status: TOAST_STATUS.FAIL,
				announcement: DELETE_CONFIRMATION_DIALOG_LABELS.TOAST_ERROR
			});
		} finally {
			setIsDeleting(false);
		}
	};
	const handleClick = (
		e: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>
	): void => {
		e.stopPropagation();
		if ('key' in e) {
			isTabKeyRef.current = e.key === 'Tab';
		}
	};

	const handleBlur = (e: React.FocusEvent<HTMLDivElement>): void => {
		if (!isTabKeyRef.current && isMenuOpenRef.current) {
			e.currentTarget.querySelector('button')?.click();
		}
		isTabKeyRef.current = false;
	};
	return (
		// eslint-disable-next-line jsx-a11y/no-static-element-interactions
		<div onClick={handleClick} onBlur={handleBlur} onKeyDown={handleClick}>
			<EvDropdownMenu
				id={id}
				triggerButtonProps={{
					type: BUTTON_TYPES.ICON,
					title: triggerAriaLabel,
					children: <EvIcon icon={ThreeDotsIcon} small />
				}}
				onDropdownOpenStateChange={(isOpen) => {
					isMenuOpenRef.current = isOpen;
				}}
				options={[
					{
						id: 'delete',
						type: DropdownOptionType.BUTTON,
						title: deleteLabel,
						disabled: isDeleteDisabled,
						buttonProps: {
							type: BUTTON_TYPES.ICON,
							title: deleteLabel,
							children: deleteLabel,
							onClick: () => setIsConfirmOpen(true)
						}
					}
				]}
			/>
			<DeleteConfirmationDialog
				isOpen={isConfirmOpen}
				onConfirm={handleConfirm}
				onCancel={() => setIsConfirmOpen(false)}
				isDeleting={isDeleting}
				entityName={entityName}
				isCentered
				isBgTransparent
			/>
		</div>
	);
};
